
import { Channel, ChatRoom, ChatRoomType } from '@dinosband/bv-main-schema/dist/types'
import { BvDataCenterContext } from '../..'
import { ChannelChatRooms } from './channel-chat-rooms'
import { CommentsChatRooms } from './comments-chat-rooms'
import { PrivateChatRooms } from './private-chat-rooms'
import { SupplementChatRooms } from './supplement-chat-rooms'
import { CreatePrivateChatRoomArgs } from '@dinosband/bv-main-client'
import { HelpDeskChatRooms } from './help-desk-chat-rooms'

export class ChatRooms {

  context: BvDataCenterContext

  _channelChatRoomsPool: ChannelChatRooms[] = []
  _privateChatRooms: PrivateChatRooms
  _commentsChatRooms: CommentsChatRooms
  _helpDeskChatRooms: HelpDeskChatRooms
  _supplementChatRooms: SupplementChatRooms

  constructor (context: BvDataCenterContext) {
    this.context = context
    this._privateChatRooms = new PrivateChatRooms(context)
    this._helpDeskChatRooms = new HelpDeskChatRooms(context)
    this._commentsChatRooms = new CommentsChatRooms(context)
    this._supplementChatRooms = new SupplementChatRooms(context)
  }

  async init () {
    await Promise.all([
      this._privateChatRooms.init(),
      this._helpDeskChatRooms.init(),
    ])
  }

  cleanup () {
    this._channelChatRoomsPool.forEach((i) => i.cleanup())
    this._channelChatRoomsPool = []
    this._privateChatRooms.cleanup()
    this._helpDeskChatRooms.cleanup()
  }

  get privateChatRooms () {
    return this._privateChatRooms.chatRooms
  }

  get helpDeskChatRooms () {
    return this._helpDeskChatRooms.chatRooms
  }

  chatRoom (chatRoomId: number) {
    return this.privateChatRoom(chatRoomId) ||
           this.channelChatRoom(chatRoomId) ||
           this._supplementChatRooms.get(chatRoomId)
  }

  privateChatRoom (chatRoomId: number) {
    return this._privateChatRooms.get(chatRoomId)
  }

  commentsChatRoom (chatRoomId: number) {
    return this._commentsChatRooms.get(chatRoomId)
  }

  helpDeskChatRoom (chatRoomId: number) {
    return this._helpDeskChatRooms.get(chatRoomId)
  }

  channelChatRoom (chatRoomId: number) {
    for (const pool of this._channelChatRoomsPool) {
      const found = pool.get(chatRoomId)
      if (found) {
        return found
      }
    }
  }

  async readChatRoom (chatRoomId: number) {
    return this.channelChatRoom(chatRoomId) ||
           this.privateChatRoom(chatRoomId) ||
           this.commentsChatRoom(chatRoomId) ||
           this.helpDeskChatRoom(chatRoomId) ||
           await this._supplementChatRooms.read(chatRoomId)
  }

  findPrivateChatRoom (profiles: number[], rootMessageId?: number) {
    return this._privateChatRooms.findChatRoom(profiles, rootMessageId)
  }

  findPrivateChatRooms (profiles: number[], rootMessageId?: number) {
    return this._privateChatRooms.findChatRooms(profiles, rootMessageId)
  }

  async findGroupChatRoom (rootMessageId: number) {
    return await this._privateChatRooms.findGroupChatRoom(rootMessageId)
  }

  async findCommentsChatRoom (arg: { chatRoomId?: number, rootMessageId?: number}) {
    return await this._commentsChatRooms.findChatRoom(arg)
  }

  async requestPrivateChatRoom (args: CreatePrivateChatRoomArgs) {
    return await this._privateChatRooms.requestPrivateChatRoom(args)
  }

  private async requestGroupChatRoom (args: CreatePrivateChatRoomArgs) {
    return await this._privateChatRooms.requestGroupChatRoom(args)
  }

  private async requestCommentsChatRoom (commentsRootMessageId: number, commentsRootChatRoomId: number, channelId: number) {
    return await this._commentsChatRooms.requestCommentsChatRoom(commentsRootMessageId, commentsRootChatRoomId, channelId)
  }

  private async requestHelpDeskChatRoom (channelId: number, profileId: number) {
    return await this._helpDeskChatRooms.requestChatRoom(channelId, profileId)
  }

  async requestChatRoom (type: ChatRoomType, args: CreatePrivateChatRoomArgs): Promise<ChatRoom | undefined> {
    if (type === ChatRoomType.ONE_TO_ONE) {
      if (!args.targetId || !args.targetProfileId) {
        console.error('[DataCenter]', 'ChatRooms', 'requestChatRoom', 'target is null')
        return
      }
      return await this.requestPrivateChatRoom(args)
    } else if (type === ChatRoomType.JOB_GROUP) {
      const { jobOffer, jrsp } = args
      if (!jobOffer && !jrsp) {
        console.error('[DataCenter]', 'ChatRooms', 'requestChatRoom', 'jobOffer is null')
        return
      }
      return await this.requestGroupChatRoom(args)
    } else if (type === ChatRoomType.COMMENTS) {
      const { rootMessageId, rootChatRoomId, channelId } = args
      if (!rootMessageId || !rootChatRoomId) {
        console.error('[DataCenter]', 'ChatRooms', 'requestChatRoom', 'rootMessage is null')
        return
      }
      return await this.requestCommentsChatRoom(rootMessageId, rootChatRoomId, channelId ?? 0)
    } else if (type == ChatRoomType.HELPDESK) {
      const { channelId, requesterProfileId } = args
      if (!channelId || !requesterProfileId) {
        console.error('[DataCenter]', 'ChatRooms', 'requestChatRoom', 'helpdesk', channelId, requesterProfileId)
        return
      }
      return await this.requestHelpDeskChatRoom(channelId, requesterProfileId)
    } else {
      console.error('[DataCenter]', 'ChatRooms', 'requestChatRoom', 'invalid type', type)
    }
  }

  async initChannelChatRooms (channel: Channel) {
    const already = this._channelChatRoomsPool.find((i) => i.channelId === channel.id)
    if (already) {
      return
    }
    const channelChatRooms = new ChannelChatRooms(channel, this.context)
    await channelChatRooms.init()
    this._channelChatRoomsPool.push(channelChatRooms)
    channelChatRooms.publish()
    return channelChatRooms.chatRooms.map((i) => i.id)
  }

  async cleanupChannelChatRooms (channelId: number) {
    const selected = this._channelChatRoomsPool.find((i) => i.channelId === channelId)
    if (!selected) {
      return
    }
    selected.chatRooms.forEach((i) => {
      this._supplementChatRooms.set(i.id, i)
    })
    selected.cleanup()
    this._channelChatRoomsPool = this._channelChatRoomsPool.filter((i) => i.channelId !== channelId)
  }

  onReconnected () {
    this._privateChatRooms.onReconnected()
    this._helpDeskChatRooms.onReconnected()
  }

}
